import { useCallback, useEffect, useState } from 'react';
import { stateSetter } from '@/utils';
// Import DIRECT (nu prin barrel-ul '@/components') — evită ciclul hooks ↔ components.
import { IServerColumnConfig } from '@/components/table/column-config';
import { ICreateFSP, IFSP, IUpdateFSP, FSPsReturnType } from './types';
import {
	deleteFSP,
	fetchAdministrationsTableConfig,
	fetchFSPs,
	fetchQueryFSPs,
	fetchSingleFSP,
	patchFSP,
	postFSP
} from './actions';

interface ILoading {
	all: boolean;
	delete: boolean;
	create: boolean;
	update: boolean;
}

// Fallback-ul pentru `data` încă neîncărcat trebuie să fie STABIL ca identitate:
// un `[]` nou la fiecare randare declanșa la nesfârșit efectele consumatorilor cu
// dependință pe `data` → „Maximum update depth exceeded" (crash pe Administrații).
const EMPTY_FSPS: IFSP[] = [];

export function useFSPs<T extends IFSP | IFSP[]>(
	getFSPsOnRender: boolean | undefined = undefined,
	appFSPId: number | undefined = undefined,
	queryParam: string | undefined = undefined,
	getAllQuery: boolean | undefined = undefined,
	getAllFSP: boolean | undefined = undefined,
	setEnable: boolean | undefined = false,
	refresh: any | undefined = undefined,
	withTableConfig: boolean | undefined = false,
	/**
	 * Paginarea INIȚIALĂ (de regulă cea din URL, via useListQueryState). Fără ea,
	 * starea internă pornește pe 0/10 și primul fetch pleacă cu valori greșite, urmat
	 * de un al doilea după sincronizarea din efectele-punte — două call-uri și un race
	 * pe răspunsuri. Doar valoarea de la primul render contează.
	 */
	initialPagination: { page: number; items: number } | undefined = undefined
): FSPsReturnType<T> {
	const [data, setData] = useState<T | null>(null);
	// Configurația de coloane a tabelului (api/administrations/table-config) — cerută
	// doar de pagina de listă (withTableConfig); la eroare rămâne null și tabelul cade
	// silențios pe coloanele definite local.
	const [tableConfig, setTableConfig] = useState<IServerColumnConfig[] | null>(null);
	const [loadingTableConfig, setLoadingTableConfig] = useState<boolean>(
		Boolean(withTableConfig)
	);
	const [loading, setLoading] = useState<ILoading>({
		all: true,
		delete: false,
		create: false,
		update: false
	});
	const [enableQuery, setEnableQuery] = useState<boolean>(setEnable);
	const [page, setPage] = useState<number>(initialPagination?.page ?? 0);
	const [items, setItems] = useState<number>(initialPagination?.items ?? 10);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [totalItems, setTotalItems] = useState<number>(1);

	const handleResponse = (pageParam: number, itemsParam: number): void => {
		setTotalPages(pageParam);
		setTotalItems(itemsParam);
	};

	const toggleLoading = (
		type: 'all' | 'delete' | 'create' | 'update',
		value: boolean
	): void => {
		setLoading((prevState) => ({
			...prevState,
			[type]: value
		}));
	};

	const getFSPs = useCallback(
		async (allFSP: boolean, pageParam: number, itemsParam: number) => {
			try {
				toggleLoading('all', true);
				const response = await fetchFSPs(allFSP, pageParam, itemsParam, handleResponse);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				toggleLoading('all', false);
			}
		},

		[setData]
	);

	const getQueryFSPs = useCallback(
		async (allQuery: boolean, query: string, queryPage: number, queryItems: number) => {
			try {
				!enableQuery && setEnableQuery(true);
				toggleLoading('all', true);
				const response = await fetchQueryFSPs(
					allQuery,
					query,
					queryPage,
					queryItems,
					handleResponse
				);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				toggleLoading('all', false);
			}
		},

		[setData]
	);

	const getFSP = useCallback(
		async (FSPId: number) => {
			try {
				toggleLoading('all', true);
				const response = await fetchSingleFSP(FSPId);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				toggleLoading('all', false);
			}
		},

		[setData]
	);

	const removeFSP = useCallback(
		async (FSPId: number, onSuccess?: () => void) => {
			try {
				toggleLoading('delete', true);
				const response = await deleteFSP(FSPId);
				if (response === 204) {
					setData((currentData) => {
						if (Array.isArray(currentData)) {
							return currentData.filter((FSP) => FSP.id !== FSPId) as T;
						}
						return currentData;
					});
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				toggleLoading('delete', false);
			}
		},

		[setData]
	);

	const createFSP = useCallback(
		async (FSPObj: ICreateFSP, onSuccess?: () => void, onError?: () => void) => {
			try {
				toggleLoading('create', true);
				const response = await postFSP(FSPObj);
				if (response === 201) {
					onSuccess && onSuccess();
				} else if (response === 400) {
					onError && onError();
				}
			} catch {
				onError && onError();
			} finally {
				toggleLoading('create', false);
			}
		},

		[setData]
	);

	const editFSP = useCallback(
		async (
			FSPId: number,
			updatedObject: IUpdateFSP,
			onSuccess?: () => void,
			onError?: () => void
		) => {
			try {
				toggleLoading('update', true);
				const response = await patchFSP(FSPId, updatedObject);
				if (response === 200) {
					onSuccess && onSuccess();
				} else if (response === 400) {
					onError && onError();
				}
			} catch {
				onError && onError();
			} finally {
				toggleLoading('update', false);
			}
		},

		[setData]
	);

	useEffect(() => {
		if (!withTableConfig) return undefined;
		let active = true;
		setLoadingTableConfig(true);
		fetchAdministrationsTableConfig()
			.then((columns) => {
				if (active) setTableConfig(columns.length ? columns : null);
			})
			.catch(() => {
				// Fallback silențios: fără config BE, tabelul folosește coloanele locale.
				if (active) setTableConfig(null);
			})
			.finally(() => {
				if (active) setLoadingTableConfig(false);
			});
		return () => {
			active = false;
		};
	}, [withTableConfig]);

	useEffect(() => {
		if (getFSPsOnRender && queryParam === undefined) {
			getFSPs(getAllFSP || false, page, items);
		}

	}, [getFSPsOnRender, queryParam, page, items, getAllFSP, refresh]);

	useEffect(() => {
		if (appFSPId) {
			getFSP(appFSPId);
		}

	}, [appFSPId, refresh]);

	useEffect(() => {
		if (queryParam && appFSPId === undefined) {
			getQueryFSPs(getAllQuery || false, queryParam, page, items);
		} else if (appFSPId === undefined && enableQuery) {
			getFSPs(getAllFSP || false, page, items);
		}

	}, [queryParam, appFSPId, page, items, getAllFSP, getAllQuery, refresh]);

	return {
		data: data || (EMPTY_FSPS as T),
		setData: setData as stateSetter<T>,
		loading: loading.all,
		loadingDelete: loading.delete,
		loadingCreate: loading.create,
		loadingUpdate: loading.update,
		createFSP,
		editFSP,
		getFSP,
		getFSPs,
		removeFSP,
		items,
		page,
		setItems,
		setPage,
		totalPages,
		totalItems,
		tableConfig,
		loadingTableConfig
	};
}
